import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { apiClient } from './api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Types
/**
 * User type differentiation:
 * - platform_staff: Internal users (owners, managers, staff, drivers) - cannot use storefront
 * - customer: B2C and B2B shoppers - can only access storefront
 */
export type UserType = 'platform_staff' | 'customer';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: string;
  /** User type: platform_staff or customer */
  userType?: UserType;
  organizationId?: string;
  locationId?: string;
  organizationName?: string;
  locationName?: string;
  // Default address fields
  defaultStreet?: string;
  defaultCity?: string;
  defaultState?: string;
  defaultPostalCode?: string;
  defaultCountry?: string;
  // Geolocation fields
  latitude?: number;
  longitude?: number;
  // Delivery preferences
  preferDelivery?: boolean;
  preferPickup?: boolean;
  deliveryInstructions?: string;
}

export interface LoginDto {
  email: string;
  password: string;
  twoFactorCode?: string;
}

export interface RegisterDto {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
  // Optional address fields
  defaultStreet?: string;
  defaultCity?: string;
  defaultState?: string;
  defaultPostalCode?: string;
  defaultCountry?: string;
  // Optional geolocation fields
  latitude?: number;
  longitude?: number;
  // Optional delivery preferences
  preferDelivery?: boolean;
  preferPickup?: boolean;
  deliveryInstructions?: string;
}

export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  expires_in: string;
  user: User;
}

export interface Requires2FAResponse {
  requires2FA: true;
  message: string;
}

export interface Session {
  id: string;
  userAgent?: string;
  ipAddress?: string;
  createdAt: string;
  expiresAt: string;
}

export interface TwoFactorSetup {
  secret: string;
  qrCode: string;
}

export interface TwoFactorStatus {
  isEnabled: boolean;
  enabledAt?: string;
  recoveryCodesRemaining?: number;
}

// Token management
const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'user';

export const tokenManager = {
  getAccessToken: (): string | null => localStorage.getItem(TOKEN_KEY),

  getRefreshToken: (): string | null => localStorage.getItem(REFRESH_TOKEN_KEY),

  getUser: (): User | null => {
    const userStr = localStorage.getItem(USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  },

  setTokens: (accessToken: string, refreshToken: string, user: User): void => {
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },

  clearTokens: (): void => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },
};

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = (token: string) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

// Axios interceptor for automatic token refresh
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = tokenManager.getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // If error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Wait for the ongoing refresh to complete
        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            resolve(apiClient(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = tokenManager.getRefreshToken();
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        // Call refresh endpoint
        const response = await axios.post<AuthResponse>(`${API_URL}/auth/refresh`, {
          refreshToken,
        });

        const { access_token, refresh_token, user } = response.data;
        tokenManager.setTokens(access_token, refresh_token, user);

        // Notify all waiting requests
        onRefreshed(access_token);
        isRefreshing = false;

        // Retry original request with new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
        }
        return apiClient(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        tokenManager.clearTokens();
        // Don't redirect - just clear tokens and retry without auth
        // This allows public pages like /products to continue working
        // The user will be logged out but can still browse
        if (originalRequest.headers) {
          delete originalRequest.headers.Authorization;
        }
        originalRequest._retry = true; // Prevent infinite loop
        return apiClient(originalRequest); // Retry without token
      }
    }

    return Promise.reject(error);
  }
);

// Auth API service
export const authService = {
  // Login
  async login(data: LoginDto): Promise<AuthResponse | Requires2FAResponse> {
    const response = await axios.post<AuthResponse | Requires2FAResponse>(
      `${API_URL}/auth/login`,
      data
    );

    // Check if 2FA is required
    if ('requires2FA' in response.data) {
      return response.data;
    }

    // Store tokens
    const authResponse = response.data as AuthResponse;
    tokenManager.setTokens(
      authResponse.access_token,
      authResponse.refresh_token,
      authResponse.user
    );

    return authResponse;
  },

  // Register
  async register(data: RegisterDto): Promise<User> {
    const response = await axios.post<User>(`${API_URL}/auth/register`, data);
    return response.data;
  },

  // Logout
  async logout(): Promise<void> {
    const refreshToken = tokenManager.getRefreshToken();
    if (refreshToken) {
      try {
        await axios.post(`${API_URL}/auth/logout`, { refreshToken });
      } catch (error) {
        // Ignore logout errors
        console.error('Logout error:', error);
      }
    }
    tokenManager.clearTokens();
  },

  // Logout from all devices
  async logoutAll(): Promise<void> {
    await apiClient.post('/auth/logout-all');
    tokenManager.clearTokens();
  },

  // Manually refresh token
  async refreshToken(): Promise<AuthResponse> {
    const refreshToken = tokenManager.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await axios.post<AuthResponse>(`${API_URL}/auth/refresh`, {
      refreshToken,
    });

    tokenManager.setTokens(
      response.data.access_token,
      response.data.refresh_token,
      response.data.user
    );

    return response.data;
  },

  // Get current user
  getCurrentUser(): User | null {
    return tokenManager.getUser();
  },

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!tokenManager.getAccessToken();
  },

  // Session Management
  async getSessions(): Promise<Session[]> {
    const response = await apiClient.get<Session[]>('/auth/sessions');
    return response.data;
  },

  async revokeSession(sessionId: string): Promise<void> {
    await apiClient.delete(`/auth/sessions/${sessionId}`);
  },

  async revokeAllSessions(): Promise<void> {
    await apiClient.delete('/auth/sessions');
  },

  // Two-Factor Authentication
  async setup2FA(): Promise<TwoFactorSetup> {
    const response = await apiClient.get<TwoFactorSetup>('/auth/2fa/setup');
    return response.data;
  },

  async enable2FA(token: string): Promise<{ recoveryCodes: string[]; message: string }> {
    const response = await apiClient.post<{ recoveryCodes: string[]; message: string }>(
      '/auth/2fa/enable',
      { token }
    );
    return response.data;
  },

  async disable2FA(token: string): Promise<{ message: string }> {
    const response = await apiClient.delete<{ message: string }>('/auth/2fa/disable', {
      data: { token },
    });
    return response.data;
  },

  async verify2FA(token: string): Promise<{ valid: boolean; message: string }> {
    const response = await apiClient.post<{ valid: boolean; message: string }>(
      '/auth/2fa/verify',
      { token }
    );
    return response.data;
  },

  async verifyRecoveryCode(
    recoveryCode: string
  ): Promise<{ valid: boolean; message: string }> {
    const response = await apiClient.post<{ valid: boolean; message: string }>(
      '/auth/2fa/verify-recovery',
      { recoveryCode }
    );
    return response.data;
  },

  async get2FAStatus(): Promise<TwoFactorStatus> {
    const response = await apiClient.get<TwoFactorStatus>('/auth/2fa/status');
    return response.data;
  },

  // Password Reset
  async requestPasswordReset(email: string): Promise<{ message: string }> {
    const response = await axios.post<{ message: string }>(
      `${API_URL}/auth/forgot-password`,
      { email }
    );
    return response.data;
  },

  async validateResetToken(token: string): Promise<{ valid: boolean; email?: string }> {
    const response = await axios.get<{ valid: boolean; email?: string }>(
      `${API_URL}/auth/reset-password/validate/${token}`
    );
    return response.data;
  },

  async resetPassword(token: string, password: string): Promise<{ message: string }> {
    const response = await axios.post<{ message: string }>(
      `${API_URL}/auth/reset-password`,
      { token, password }
    );
    return response.data;
  },
};
